<template>
	<v-sheet v-if="tbody.length > 0" :class="{ 'internal-table-listing': internal }">
		<template v-if="!pageLoading">
			<v-simple-table class="bt-table">
				<template v-slot:default>
					<thead>
						<tr>
							<th v-for="(th, key) in sortedThead" :key="key" :width="th.width">
								<v-layout>
									<v-flex class="text-left">
										<template v-if="th.checkbox">
											<v-checkbox
												dense
												v-model="checkbox"
												hide-details
												class="m-0 p-0"
												v-on:change="selectAll"
												:indeterminate="indeterminate"
											></v-checkbox>
										</template>
										<template v-else>
											{{ th.name }}
										</template>
									</v-flex>
									<v-flex v-if="!th.checkbox && th.sortable" class="text-right ml-2">
										<template v-if="th.sort == 'ASC'">
											<v-icon small link>mdi-sort-ascending</v-icon>
										</template>
										<template v-if="th.sort == 'DESC'">
											<v-icon small link>mdi-sort-descending</v-icon>
										</template>
									</v-flex>
								</v-layout>
							</th>
						</tr>
					</thead>
					<tbody>
						<template v-for="(td, bkey) in tbody">
							<v-hover :key="`row-hover-${bkey}`" v-slot="{ hover }">
								<tr :key="`row-${bkey}`">
									<template v-for="(th, hkey) in sortedThead">
										<td
											:key="`first-${hkey}-${bkey}`"
											:width="th.width"
											v-on:click="routeToDetail(th.order, td.uuid, th.key)"
											:class="{ 'td-link': th.order > 1 }"
										>
											<template v-if="th.checkbox">
												<v-checkbox
													dense
													v-model="selectedRows"
													:value="td.uuid"
													hide-details
													class="m-0 p-0"
													multiple
													v-on:change="updateSelected()"
												>
												</v-checkbox>
											</template>
											<template v-else-if="th.key == 'action'">
												<template
													v-if="
														!disableEdit &&
														getPermission(`${type}:update`) &&
														type != 'checkout' &&
														type != 'reservation' &&
														checkWarranty(td)
													"
												>
													<v-tooltip top content-class="custom-top-tooltip">
														<template v-slot:activator="{ on, attrs }">
															<v-btn
																depressed
																fab
																:dark="!is_edit_disabled(td)"
																:disabled="is_edit_disabled(td)"
																x-small
																color="green"
																v-bind="attrs"
																v-on="on"
																class="mr-3"
																v-on:click.stop.prevent="routeToUpdate(td.uuid)"
															>
																<v-icon>mdi-pencil</v-icon>
															</v-btn>
														</template>
														<span>Edit {{ type }}</span>
													</v-tooltip>
												</template>
												<template
													v-if="getPermission(`${type}:delete`) && type != 'property' && checkWarranty(td)"
												>
													<v-tooltip top content-class="custom-top-tooltip">
														<template v-slot:activator="{ on, attrs }">
															<v-btn
																depressed
																fab
																:dark="!is_delete_disabled(td)"
																x-small
																:disabled="is_delete_disabled(td)"
																color="red"
																v-bind="attrs"
																v-on="on"
																v-on:click.stop.prevent="deleteConfirm(td)"
															>
																<v-icon>mdi-delete</v-icon>
															</v-btn>
														</template>
														<span>Delete {{ type }}</span>
													</v-tooltip>
												</template>

												<v-chip
													color="blue"
													v-if="type == 'checkout' && is_checkin(td)"
													v-on:click.stop.prevent="createCheckInOut(td)"
													class="ml-3"
													text-color="white"
													label
												>
													Receive
												</v-chip>

												<v-tooltip top content-class="custom-top-tooltip" v-if="type == 'reservation' && false">
													<template v-slot:activator="{ on, attrs }">
														<v-btn
															depressed
															fab
															dark
															x-small
															color="blue"
															v-bind="attrs"
															class="ml-3"
															v-on="on"
															v-on:click.stop.prevent="createCheckInOut(td)"
														>
															<v-icon>mdi-share-outline mdi-flip-r</v-icon>
														</v-btn>
													</template>
													<span>Issue</span>
												</v-tooltip>
												<v-tooltip
													top
													content-class="custom-top-tooltip"
													v-if="
														type == 'warranty' &&
														!td.is_extended &&
														(td.status_value == 'active' || td.status_value == 'extended')
													"
												>
													<template v-slot:activator="{ on, attrs }">
														<v-btn
															depressed
															fab
															dark
															x-small
															color="blue"
															v-bind="attrs"
															class="ml-3"
															v-on="on"
															v-on:click.stop.prevent="extendWarranty(td.uuid)"
														>
															<v-icon>mdi-share-outline mdi-flip-r</v-icon>
														</v-btn>
													</template>
													<span>Extend Warranty</span>
												</v-tooltip>
											</template>
											<template v-else-if="th.key == 'reservation_dates'">
												<p class="m-0 custom-nowrap-ellipsis" v-if="td.reserve_from_formatted">
													<b>From: </b> {{ td.reserve_from_formatted }}
												</p>
												<p class="m-0 custom-nowrap-ellipsis" v-if="td.reserve_to_formatted">
													<b>To: </b> {{ td.reserve_to_formatted }}
												</p>
											</template>
											<template v-else-if="th.key == 'reservation_type'">
												<div>
													<div class="mb-1">
														<Chip :text="td.type" :color="td.type == 'Asset' ? 'orange' : 'blue'"> </Chip>
													</div>
													<div>
														<template v-if="td.is_rent == 1">
															<Chip text="Sales/Rental" color="blue" outlined text-color=""> </Chip>
														</template>
													</div>
												</div>
											</template>
											<template v-else-if="th.key == 'retired_type'">
												<Chip
													:text="td.retired_type"
													:color="td.retired_type == 'Asset' ? 'orange' : 'blue'"
												></Chip>
											</template>
											<template v-else-if="th.key == 'image'">
												<ImageTemplate circle :width="imageWidth" :src="td.image"> </ImageTemplate>
											</template>
											<template v-else-if="th.key == 'barcode_image'">
												<div
													v-if="td.pico_barcode"
													v-on:click.stop.prevent="openPopup(td, 'barcode')"
													class="text-center"
												>
													<label class="font-size-14 mb-1">
														<v-icon size="30">mdi-barcode-scan</v-icon>
													</label>
													<div class="font-size-14 fw-600 mb-1 w-100">
														{{ td.pico_barcode }}
													</div>
												</div>
												<div v-else>
													<em class="text-muted">No Barcode</em>
												</div>
												<!-- <ImageTemplate circle style="max-width: 50px" :src="td.barcode_image"></ImageTemplate> -->
											</template>
											<template v-else-if="th.key == 'password'">
												<div
													v-if="td.password"
													v-on:click.stop.prevent="openPopup(td, 'password')"
													class="text-center"
												>
													<label class="font-size-14 fw-700 mb-1"> ******** </label>
												</div>
												<div v-else>
													<em class="text-muted">No Password</em>
												</div>
												<!-- <ImageTemplate circle style="max-width: 50px" :src="td.barcode_image"></ImageTemplate> -->
											</template>
											<template v-else-if="th.key == 'incharge_image'">
												<template v-if="td.incharge_officer">
													<div class="d-flex align-items-center">
														<div style="width: 50px">
															<ImageTemplate circle style="max-width: 50px" :src="td.incharge_image">
															</ImageTemplate>
														</div>
														<div class="ml-2">
															<template v-if="td.incharge_officer">
																<div>
																	<label class="font-size-14 mb-1"
																		><b>
																			<v-icon size="18">mdi-pound</v-icon>
																		</b></label
																	>
																	{{ td.incharge_officer }}
																</div>
															</template>

															<template v-if="td.created_by">
																<div>
																	<label class="font-size-14 mb-1"
																		><b>
																			<v-icon size="18">mdi-account-circle </v-icon>
																		</b></label
																	>
																	{{ td.created_by }}
																</div>
															</template>
															<template v-if="td.email">
																<div>
																	<label class="font-size-14 mb-1"
																		><b>
																			<v-icon size="18">mdi-email</v-icon>
																		</b></label
																	>
																	{{ td.email }}
																</div>
															</template>
														</div>
													</div>
												</template>
												<template v-else>
													<div class="text-center">
														<em class="text-muted">no inchagre officer info</em>
													</div>
												</template>
											</template>
											<template v-else-if="th.key == 'issuer_info'">
												<template v-if="td.issuer_barcode">
													<div class="d-flex align-items-center">
														<div style="width: 50px">
															<ImageTemplate circle style="max-width: 50px" :src="td.issuer_image">
															</ImageTemplate>
														</div>
														<div class="ml-2">
															<template v-if="td.issuer_barcode">
																<div>
																	<label class="font-size-14 mb-1"
																		><b>
																			<v-icon size="18">mdi-pound</v-icon>
																		</b></label
																	>
																	{{ td.issuer_barcode }}
																</div>
															</template>
															<div>
																<label class="font-size-14 mb-1"
																	><b>
																		<v-icon size="18">mdi-account-circle </v-icon>
																	</b></label
																>
																{{ td.issuer_name }}
															</div>
															<template v-if="td.issuer_email">
																<div>
																	<label class="font-size-14 mb-1"
																		><b>
																			<v-icon size="18">mdi-email</v-icon>
																		</b></label
																	>
																	{{ td.issuer_email }}
																</div>
															</template>
														</div>
													</div>
												</template>
												<template v-else>
													<div class="text-center">
														<em class="text-muted">no issuer info</em>
													</div>
												</template>
											</template>
											<template v-else-if="th.key == 'customer_display_name'">
												<template v-if="td.customer_display_name">
													<div class="d-flex align-items-center">
														<div style="width: 50px">
															<ImageTemplate circle style="max-width: 50px" :src="td.customer_image">
															</ImageTemplate>
														</div>
														<div class="ml-2">
															<div>
																<label class="font-size-14 mb-1"
																	><b>
																		<v-icon size="18">mdi-pound</v-icon>
																	</b></label
																>
																{{ td.customer_barcode }}
															</div>
															<div>
																<label class="font-size-14 mb-1"
																	><b>
																		<v-icon size="18">mdi-account-circle</v-icon>
																	</b></label
																>
																{{ td.customer_display_name }}
															</div>

															<div>
																<label class="font-size-14 mb-1"
																	><b>
																		<v-icon size="18">mdi-email</v-icon>
																	</b></label
																>
																{{ td.customer_email }}
															</div>
														</div>
													</div>
												</template>
												<template v-else>
													<div class="text-center">
														<em class="text-muted">no customer info</em>
													</div>
												</template>
											</template>
											<template v-else-if="th.key == 'supplier'">
												<div class="d-flex align-items-center">
													<div class="ml-2">
														<div>
															<label class="font-size-14 mb-1"
																><b>
																	<v-icon size="18">mdi-account-circle</v-icon>
																</b></label
															>
															{{ td.supplier }}
														</div>

														<div>
															<label class="font-size-14 mb-1"
																><b>
																	<v-icon size="18">mdi-phone</v-icon>
																</b></label
															>
															{{ td.contact }}
														</div>
													</div>
												</div>
											</template>
											<template v-else-if="th.key == 'purchased_info'">
												<div class="d-flex align-items-center">
													<div class="ml-2">
														<div v-if="td.purchased_on">
															<label class="mb-1 bold-600">Purchased On : </label>
															{{ td.purchased_on }}
														</div>
														<div v-if="td.cost_price">
															<label class="mb-1 bold-600">Cost Price : </label>
															{{ td.cost_price }}
														</div>
														<div v-if="td.supplier">
															<label class="mb-1 bold-600">Supplier : </label>
															<v-icon size="18">mdi-account-circle</v-icon> {{ td.supplier }}
														</div>
													</div>
												</div>
											</template>
											<template v-else-if="th.key == 'name' && type == 'document'">
												<div class="d-flex align-items-center">
													<div class="ml-2">
														{{ td.name }}
													</div>
												</div>
											</template>
											<template v-else-if="th.key == 'name'">
												<div class="d-flex align-items-center">
													<div class="ml-2">
														<div>
															<label class="font-size-14 mb-1"
																><b>
																	<v-icon size="18">mdi-pound</v-icon>
																</b></label
															>
															{{ td.barcode }}
														</div>

														<div style="width: 250px; white-space: break-spaces">
															<label class="mb-1 bold-600">Name : </label>
															<v-icon v-if="type != 'asset'" size="18">mdi-account-circle</v-icon>
															<span class="bold-500">{{ td.name }}</span>
														</div>
														<div v-if="td.group" style="width: 250px; white-space: break-spaces">
															<label class="mb-1 bold-600">Category : </label>
															<v-icon v-if="type != 'asset'" size="18">mdi-account-circle</v-icon>
															{{ td.group }}
														</div>
														<div>
															<label class="mb-1 d-flex">
																<div v-if="td.activated == 0" class="mr-1">
																	<Status status="inactive" type="circle"></Status>
																</div>
																<div v-if="td.activated == 1" class="mr-1">
																	<Status status="active" type="circle"></Status>
																</div>
																<div v-if="td.has_checkout == 1" class="mr-1">
																	<Status status="issued" type="circle"></Status>
																</div>
																<div v-if="td.has_service == 1" class="mr-1">
																	<Status status="under-service" type="circle"> </Status>
																</div>
																<div v-if="td.retired == 1" class="mr-1">
																	<Status status="retired" type="circle"></Status>
																</div>
															</label>
														</div>
													</div>
												</div>
											</template>
											<template v-else-if="th.key == 'service_info'">
												<div class="d-flex align-items-center">
													<div class="ml-2">
														<div>
															<label class="font-size-14 mb-1"
																><b>
																	<v-icon size="18">mdi-pound</v-icon>
																</b></label
															>
															{{ td.barcode }}
														</div>

														<div style="width: 250px; white-space: break-spaces">
															<label class="mb-1 bold-600">Type : </label>
															<span class="bold-500">{{ td.service_type }}</span>
														</div>
														<div>
															<label class="mb-1 d-flex">
																<div class="mr-1">
																	<Status :status="td.status_value" type="circle"> </Status>
																</div>
																<div v-if="td.overdue_status == 1" class="mr-1">
																	<v-tooltip top content-class="custom-top-tooltip">
																		<template v-slot:activator="{ on, attrs }">
																			<v-chip
																				v-bind="attrs"
																				v-on="on"
																				color="red"
																				style="width: 19px !important; height: 23px !important"
																			></v-chip>
																		</template>
																		<span> Overdue</span>
																	</v-tooltip>
																</div>
																<div v-if="td.has_checkout == 1" class="mr-1">
																	<Status status="issued" type="circle"></Status>
																</div>
																<div v-if="td.has_service == 1" class="mr-1">
																	<Status status="under-service" type="circle"> </Status>
																</div>
																<div v-if="td.retired == 1" class="mr-1">
																	<Status status="retired" type="circle"></Status>
																</div>
															</label>
														</div>
													</div>
												</div>
											</template>
											<template v-else-if="th.key == 'asset_info'">
												<div
													class="d-flex align-items-center"
													v-on:click="redirectToRoute('asset-detail', td.asset_uuid)"
												>
													<div class="ml-2">
														<div>
															<label class="font-size-14 mb-1"
																><b>
																	<v-icon size="18">mdi-pound</v-icon>
																</b></label
															>
															{{ td.asset_barcode }}
														</div>

														<div style="width: 250px; white-space: break-spaces">
															<label class="mb-1 bold-600">Name : </label>
															<span class="bold-500">{{ td.asset_name }}</span>
														</div>
														<div v-if="td.group" style="width: 250px; white-space: break-spaces">
															<label class="mb-1 bold-600">Category : </label>
															<v-icon v-if="type != 'asset'" size="18">mdi-account-circle</v-icon>
															{{ td.group }}
														</div>
													</div>
												</div>
											</template>
											<template v-else-if="th.key == 'warranty_equip_info'">
												<div
													class="d-flex align-items-center"
													v-on:click="redirectToRoute('asset-detail', td.asset_uuid)"
												>
													<div class="ml-2">
														<div>
															<label class="font-size-14 mb-1"
																><b>
																	<v-icon size="18">mdi-pound</v-icon>
																</b></label
															>
															{{ td.asset_barcode }}
														</div>

														<div style="width: 250px; white-space: break-spaces">
															<label class="mb-1 bold-600">Name : </label>
															<span class="bold-500">{{ td.asset_name }}</span>
														</div>
														<div v-if="td.group" style="width: 250px; white-space: break-spaces">
															<label class="mb-1 bold-600">Type : </label>
															{{ td.type }}
														</div>
													</div>
												</div>
											</template>
											<template v-else-if="th.key == 'warranty_info'">
												<div class="d-flex align-items-center">
													<div class="ml-2">
														<div>
															<label class="font-size-14 mb-1"
																><b>
																	<v-icon size="18">mdi-pound</v-icon>
																</b></label
															>
															{{ td.warranty_barcode }}
														</div>

														<div v-if="td.warranty_cost">
															<label class="mb-1 bold-600">Cost : </label>
															{{ td.warranty_cost }}
														</div>
													</div>
												</div>
											</template>
											<template v-else-if="th.key == 'type_formatted'">
												<Chip
													v-for="(row, index) in td.type_formatted"
													:key="index"
													:text="row.text"
													:color="row.color"
													class="mr-2 bg-white"
												></Chip>
											</template>
											<template v-else-if="th.key == 'related_formatted'" style="background: white">
												<!-- <Chip v-for="(row, index) in td.related_formatted" :key="index"
													:text="row.text" :color="row.color" class="mr-2 circle-border">
												</Chip> -->
												<div class="d-flex align-items-center">
													<v-avatar
														v-for="(row, index) in td.related_formatted"
														:key="index"
														:color="row.color"
														class="mr-2 circle-border"
														size="28"
													>
														<span class="white--text">
															{{ row.text }}
														</span>
													</v-avatar>
													<v-badge color="cyan" :content="td.order_item_count"></v-badge>
												</div>
											</template>
											<template v-else-if="th.key == 'barcode'">
												<Chip :text="td.barcode" color="cyan"></Chip>
											</template>
											<template v-else-if="th.key == 'member_barcode'">
												<template v-if="td.member_barcode">
													<div class="d-flex align-items-center">
														<div style="width: 50px">
															<ImageTemplate circle style="max-width: 50px" :src="td.image"> </ImageTemplate>
														</div>
														<div class="ml-2">
															<div>
																<label class="font-size-14 mb-1"
																	><b>
																		<v-icon size="18">mdi-pound</v-icon>
																	</b></label
																>
																{{ td.member_barcode }}
															</div>

															<div>
																<label class="font-size-14 mb-1"
																	><b>
																		<v-icon size="18">mdi-account-circle</v-icon>
																	</b></label
																>
																{{ td.display_name }}
															</div>
															<div>
																<label class="font-size-14 mb-1"
																	><b>
																		<v-icon size="18">mdi-email</v-icon>
																	</b></label
																>
																{{ td.email }}
															</div>
														</div>
													</div>
												</template>
												<template v-else>
													<div class="text-center">
														<em class="text-muted">no Staff info</em>
													</div>
												</template>
											</template>
											<template v-else-if="th.key == 'warranty_start_date'">
												<div v-if="td.warranty_start_date" class="d-flex align-items-center">
													<div
														:class="`ml-2 ${
															td.isWarrantyExpired == 1 ? 'expired' : td.isWarrantyRenewed == 1 ? 'renewed' : ''
														}`"
													>
														<div>
															<v-tooltip top content-class="custom-top-tooltip">
																<template v-slot:activator="{ on, attrs }">
																	<label v-bind="attrs" v-on="on" class="font-size-14 mb-1 mr-7">
																		<v-badge color="cyan" content="S" class="badge-new"></v-badge>
																	</label>
																</template>
																<span> Start Date </span>
															</v-tooltip>
															<v-chip label class="mb-2">
																{{ td.warranty_start_date }}
															</v-chip>
														</div>

														<div>
															<v-tooltip top content-class="custom-top-tooltip">
																<template v-slot:activator="{ on, attrs }">
																	<label v-bind="attrs" v-on="on" class="font-size-14 mb-1 mr-7">
																		<v-badge color="red" content="E" class="badge-new"></v-badge>
																	</label>
																</template>
																<span> End Date </span>
															</v-tooltip>
															<v-chip label class="mb-2">
																{{ td.warranty_end_date }}
															</v-chip>
														</div>
														<div>
															<v-tooltip top content-class="custom-top-tooltip">
																<template v-slot:activator="{ on, attrs }">
																	<label v-bind="attrs" v-on="on" class="font-size-14 mb-1 mr-7">
																		<v-badge color="orange" content="R" class="badge-new"></v-badge>
																	</label>
																</template>
																<span> Reminder Date </span>
															</v-tooltip>
															<v-chip label class="mb-2">
																{{ td.warranty_reminder_date }}
															</v-chip>
														</div>
													</div>
												</div>
												<div v-else>
													<span class="text-truncate" v-html="'No Warranty Data'"> </span>
												</div>
											</template>
											<template v-else-if="th.key == 'expected_date'">
												<div v-if="td.expected_start_date_formatted" class="d-flex align-items-center">
													<div class="ml-2">
														<div>
															<v-tooltip top content-class="custom-top-tooltip">
																<template v-slot:activator="{ on, attrs }">
																	<label v-bind="attrs" v-on="on" class="font-size-14 mb-1 mr-7">
																		<v-badge color="cyan" content="S" class="badge-new"></v-badge>
																	</label>
																</template>
																<span> Start Date </span>
															</v-tooltip>
															<v-chip label class="mb-2">
																{{ td.expected_start_date_formatted }}
															</v-chip>
														</div>

														<div>
															<v-tooltip top content-class="custom-top-tooltip">
																<template v-slot:activator="{ on, attrs }">
																	<label v-bind="attrs" v-on="on" class="font-size-14 mb-1 mr-7">
																		<v-badge color="red" content="E" class="badge-new"></v-badge>
																	</label>
																</template>
																<span> End Date </span>
															</v-tooltip>
															<v-chip v-if="td.expected_end_date_formatted" label class="mb-2">
																{{ td.expected_end_date_formatted }}
															</v-chip>
														</div>
													</div>
												</div>
												<div v-else>
													<span class="text-truncate" v-html="'No Expected Data'"> </span>
												</div>
											</template>
											<template v-else-if="th.key == 'actual_date'">
												<div v-if="td.actual_start_date_formatted" class="d-flex align-items-center">
													<div class="ml-2">
														<div>
															<v-tooltip top content-class="custom-top-tooltip">
																<template v-slot:activator="{ on, attrs }">
																	<label v-bind="attrs" v-on="on" class="font-size-14 mb-1 mr-7">
																		<v-badge color="cyan" content="S" class="badge-new"></v-badge>
																	</label>
																</template>
																<span> Start Date </span>
															</v-tooltip>
															<v-chip label class="mb-2">
																{{ td.actual_start_date_formatted }}
															</v-chip>
														</div>

														<div>
															<v-tooltip top content-class="custom-top-tooltip">
																<template v-slot:activator="{ on, attrs }">
																	<label v-bind="attrs" v-on="on" class="font-size-14 mb-1 mr-7">
																		<v-badge color="red" content="E" class="badge-new"></v-badge>
																	</label>
																</template>
																<span> End Date </span>
															</v-tooltip>
															<v-chip v-if="td.actual_end_date_formatted" label class="mb-2">
																{{ td.actual_end_date_formatted }}
															</v-chip>
														</div>
													</div>
												</div>
												<div v-else>
													<span class="text-truncate" v-html="'No Actual Data'"> </span>
												</div>
											</template>
											<template v-else-if="th.key == 'status'">
												<v-col md-12>
													<Status :status="td.status_value" :type="type"></Status>
												</v-col>
												<template v-if="type == 'incharge-officer' && td.is_resigned == 1">
													<v-col md-12>
														<Chip text="Resigned" color="orange"></Chip>
													</v-col>
												</template>
												<template v-if="type == 'checkout' || type == 'checkin'">
													<v-col md-12>
														<template v-if="td.is_signed">
															<Chip text="Signed" color="green"></Chip>
														</template>
														<template v-else>
															<Chip text="Not Signed" color="orange"></Chip>
														</template>
													</v-col>
												</template>
												<template v-if="td.is_fault">
													<v-col md-12>
														<Chip text="Fault" color="#1bc5bd"></Chip>
													</v-col>
												</template>
											</template>

											<template v-else-if="th.key == 'type'">
												<template v-if="td.type == 'Issue'">
													<v-col md-12>
														<Chip text="Issue" color="red"></Chip>
													</v-col>
												</template>
												<template v-if="td.type == 'Receive'">
													<v-col md-12>
														<Chip text="Receive" color="orange"></Chip>
													</v-col>
												</template>
												<template v-if="td.type == 'Retired'">
													<v-col md-12>
														<Chip text="Retired" color="green"></Chip>
													</v-col>
												</template>
											</template>

											<template v-else-if="th.key == 'status_value'">
												<template v-if="td.status_value == 'active'">
													<v-col md-12>
														<Chip text="Active" color="green"></Chip>
													</v-col>
												</template>
												<template v-if="td.status_value == 'inactive'">
													<v-col md-12>
														<Chip text="Inactive" color="orange"></Chip>
													</v-col>
												</template>
												<template v-if="td.status_value == 'retired'">
													<v-col md-12>
														<Chip text="Retired" color="red"></Chip>
													</v-col>
												</template>
												<template> </template>
											</template>

											<template v-else-if="th.key == 'member_display_name'">
												<template v-if="td.member_display_name">
													<div class="d-flex align-items-center">
														<div style="width: 50px">
															<ImageTemplate circle style="max-width: 50px" :src="td.member_image">
															</ImageTemplate>
														</div>
														<div class="ml-2">
															<div>
																<label class="font-size-14 mb-1"
																	><b>
																		<v-icon size="18">mdi-pound</v-icon>
																	</b></label
																>
																{{ td.member_barcode }}
															</div>

															<div>
																<label class="font-size-14 mb-1"
																	><b>
																		<v-icon size="18">mdi-account-circle</v-icon>
																	</b></label
																>
																{{ td.member_display_name }}
															</div>
															<div>
																<label class="font-size-14 mb-1"
																	><b>
																		<v-icon size="18">mdi-email</v-icon>
																	</b></label
																>
																{{ td.member_email }}
															</div>
														</div>
													</div>
												</template>
												<template v-else>
													<div class="text-center">
														<em class="text-muted">no Staff info</em>
													</div>
												</template>
											</template>
											<template v-else-if="th.key == 'signed_pdf'">
												<v-icon
													v-if="td.signed_pdf"
													v-on:click.stop.prevent="downloadFile({ url: td.signed_pdf })"
													class="red--text"
													large
													>mdi-file-pdf-box</v-icon
												>
												<span v-else class="text--secondary">-</span>
											</template>
											<template v-else-if="th.key == 'order_receive_pdf'">
												<v-icon
													v-if="td.has_checkin_pdf"
													v-on:click.stop.prevent="downloadPdf(td, 'order-receive-pdf')"
													:class="td.pdf_signature == 0 ? '' : 'red--text'"
													large
												>
													mdi-file-pdf-box</v-icon
												>
												<span v-else class="text--secondary">-</span>
											</template>
											<template v-else-if="th.key == 'order_issue_pdf'">
												<v-icon
													v-if="td.has_checkout_pdf"
													v-on:click.stop.prevent="downloadPdf(td, 'order-issue-pdf')"
													:class="td.pdf_signature == 0 ? '' : 'red--text'"
													large
												>
													mdi-file-pdf-box</v-icon
												>
												<span v-else class="text--secondary">-</span>
											</template>
											<template v-else-if="th.key == 'order_retire_pdf'">
												<v-icon
													v-if="td.has_retire_pdf"
													v-on:click.stop.prevent="downloadPdf(td, 'order-retire-pdf')"
													:class="td.pdf_signature == 0 ? '' : 'red--text'"
													large
												>
													mdi-file-pdf-box</v-icon
												>
												<span v-else class="text--secondary">-</span>
											</template>
											<template v-else-if="th.key == 'file'">
												<v-btn color="blue darken-4 text-white" icon v-on:click.stop.prevent="downloadFile(td)">
													<v-icon>mdi-download</v-icon>
												</v-btn>
											</template>
											<template v-else-if="th.key == 'created_at'">
												<div>
													<label class="mb-1 bold-600 mr-1">At : </label>
													<TableDateTime
														v-if="td.created_at"
														:human-format="td.created_at"
														:system-format="td.added_at"
													></TableDateTime>
												</div>
												<div v-if="td.created_by">
													<label class="mb-1 bold-600 mr-1">By : </label>
													<v-icon size="18">mdi-account-circle </v-icon>
													{{ td.created_by }}
												</div>
											</template>
											<template v-else-if="th.key == 'modified_at'">
												<TableDateTime
													v-if="td.modified_at"
													:human-format="td.modified_at"
													:system-format="td.updated_at"
												></TableDateTime>
												<span v-else>-</span>
											</template>
											<template v-else-if="th.key == 'retired_attachment'">
												<template v-if="td.retired_attachment && td.retired_attachment.length">
													<v-chip
														v-for="(row, index) in td.retired_attachment"
														:key="index"
														v-on:click.stop.prevent="downloadFile(row)"
														class="mr-2"
														>{{ row.name }}</v-chip
													>
												</template>
												<ShowValue v-else :object="td" object-key="nothing-found" :label="th.name"></ShowValue>
											</template>
											<template v-else>
												<ShowValue :object="td" :object-key="th.key" :label="th.name"> </ShowValue>
											</template>
										</td>
									</template>
								</tr>
							</v-hover>
						</template>
					</tbody>
				</template>
			</v-simple-table>
			<v-row class="my-2">
				<v-col md="6" class="my-auto">
					<p class="m-0">{{ showingString }}</p>
				</v-col>
				<v-col md="6" class="text-right">
					<v-pagination
						color="blue darken-4"
						v-model="page"
						:length="totalPage"
						total-visible="9"
						v-on:input="updatePagination($event)"
					></v-pagination>
				</v-col>
			</v-row>
		</template>
		<div v-else class="mx-20 my-40 text-center">
			<v-progress-linear color="orange darken-2" indeterminate height="6"></v-progress-linear>
		</div>
		<Dialog :dialog="deleteDialog" :dialog-width="640">
			<template v-slot:title> Delete {{ type }}</template>
			<template v-slot:body>
				<v-row class="delete-dialog">
					<v-col md="2" class="py-0 text-right my-auto">
						<span class="svg-icon svg-icon-lg delete-confirm-icon">
							<!--begin::Svg Icon-->
							<inline-svg :src="$assetURL('media/custom-svg/attention-circle.svg')" />
							<!--end::Svg Icon-->
						</span>
					</v-col>
					<v-col md="10" class="py-0 my-auto">
						<p class="btx-p m-0">
							<span class="text-capitalize">{{ type }} </span>
							<span class="red--text font-bolder lighten-1">#{{ deleteText }}</span>
							will be deleted forever and cannot be retrieved later. Are you sure about deleting it?
						</p>
					</v-col>
					<v-col v-if="deleteNote" md="12" class="pb-0 mt-2 my-auto">
						<em>Note: {{ deleteNote }}</em>
					</v-col>
				</v-row>
			</template>
			<template v-slot:action>
				<v-btn
					class="white--text"
					:loading="deleteButton"
					:disabled="deleteButton"
					depressed
					color="red lighten-1"
					tile
					v-on:click="deleteRow"
				>
					Yes! Delete
				</v-btn>
				<v-btn depressed tile :disabled="deleteButton" v-on:click="deleteClear"> No, Close </v-btn>
			</template>
		</Dialog>

		<v-dialog
			origin="center center"
			v-model="barcodeDialog"
			persistent
			scrollable
			content-class="common-dialog"
			:max-width="500"
		>
			<v-card>
				<v-card-title
					class="headline dialog-custom-header text-uppercase custom-border-bottom custom-header-blue-text white-background"
				>
					{{ barcodeDialogTitle }}
				</v-card-title>
				<v-card-text>
					<div class="text-center mt-2">
						<img v-if="popup_image" class="mb-4" :src="popup_image" :alt="popup_text" width="400px" />
						<h3>{{ popup_text }}</h3>
					</div>
				</v-card-text>
				<v-divider class="my-0"></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn depressed tile v-on:click="barcodeDialog = false">Close </v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-sheet>
	<v-sheet v-else>
		<p class="m-0 row-not-found text-center">
			<img :src="$assetURL('media/error/empty.png')" class="row-not-found-image" />
			Uhh... There are no {{ type }} at the moment.
		</p>
	</v-sheet>
</template>
<script>
import { toSafeInteger, isArray } from "lodash";
import { mapGetters } from "vuex";
import { SET_SELECTED_ROWS, SET_CURRENT_PAGE } from "@/core/services/store/listing.module";
import Chip from "@/view/components/Chip";
import Status from "@/view/components/Status";
import Dialog from "@/view/components/Dialog";
import TableDateTime from "@/view/components/TableDateTime";
import ApiService from "@/core/services/api.service";
import ShowValue from "@/view/components/ShowValue";
import ImageTemplate from "@/view/components/Image";
import { CART_COUNT } from "@/core/services/store/config.module";
import { getToken } from "@/core/services/jwt.service.js";
//import Barcode from "@/view/components/Barcode";

export default {
	name: "table-template",
	props: {
		type: {
			type: String,
			default: null,
		},
		deleteEndpoint: {
			type: String,
			default: null,
		},
		detailRoute: {
			type: String,
			default: null,
		},
		updateRoute: {
			type: String,
			default: null,
		},
		deleteNote: {
			type: String,
			default: null,
		},
		dialogUpdate: {
			type: Boolean,
			default: false,
		},
		disableEdit: {
			type: Boolean,
			default: false,
		},
		dialogView: {
			type: Boolean,
			default: false,
		},
		internal: {
			type: Boolean,
			default: false,
		},
		pageLoading: {
			type: Boolean,
			default: false,
		},
		imageWidth: {
			type: String,
			default: "50",
		},
	},
	data() {
		return {
			checkbox: false,
			indeterminate: false,
			popup_image: null,
			barcodeDialogTitle: null,
			popup_text: null,
			selectedRows: [],
			deleteDialog: false,
			deleteButton: false,
			deleteUUID: null,
			deleteURL: null,
			deleteText: null,
			page: 1,
			barcodeDialog: false,
		};
	},
	watch: {
		currentPage(param) {
			this.page = param;
		},
		selected(param) {
			if (param.length < 1) {
				this.checkbox = false;
				this.indeterminate = false;
				this.selectedRows = [];
			}
		},
	},
	methods: {
		checkWarranty(data) {
			if (data.status_value != "draft" && this.type == "warranty") {
				return false;
			}
			return true;
		},
		extendWarranty(uuid) {
			this.$router.push({
				name: "extendWarranty",
				query: { t: new Date().getTime(), extendId: uuid },
			});
		},
		redirectToRoute(name, uuid) {
			if (uuid) {
				this.$router.push({
					name: name,
					params: { uuid },
					query: { t: new Date().getTime() },
				});
			}
		},
		openPopup(data, type) {
			if (type == "password") {
				this.popup_image = null;
				this.popup_text = data.password;
			} else {
				this.popup_image = data.barcode_image;
				this.popup_text = data.pico_barcode;
			}
			this.barcodeDialogTitle = `${data.name ? data.name : ""} (#${data.barcode})`;
			this.barcodeDialog = true;
			return false;
		},
		updatePagination(param) {
			if (this.currentPage == param) {
				return false;
			}
			this.$store.commit(SET_CURRENT_PAGE, param);
			this.$nextTick(() => {
				this.$emit("reload:content", true);
			});
		},
		downloadFile(row) {
			window.open(row.url, "_blank");
		},
		downloadPdf(param, type) {
			if (type == "order-receive-pdf") {

				if (!param.has_checkin_pdf) {
					return false;
				}
			}
			if (type == "order-issue-pdf") {
				if (!param.has_checkout_pdf) {
					return false;
				}
			}
			if (type == "order-retire-pdf") {

				if (!param.has_retire_pdf) {
					return false;
				}
			}

			const token = getToken();
			const url = `${this.$apiURL()}${type}/${param.id}/download?token=${token}`;			
			window.open(url, "_blank");
		},
		routeToDetail(order, uuid, key) {
			if (order < 2 || key == "asset_info") {
				return false;
			}
			if (!this.dialogView) {
				if (this.detailRoute) {
					if (this.internal) {
						let routeData = this.$router.resolve({
							name: this.detailRoute,
							params: { uuid },
							query: { t: new Date().getTime() },
						});
						window.open(routeData.href, "_blank");
					} else {
						this.$router.push({
							name: this.detailRoute,
							params: { uuid },
							query: { t: new Date().getTime() },
						});
					}
				}
			} else {
				this.$emit("view:dialog", uuid);
				return false;
			}
		},
		routeToUpdate(uuid) {
			if (this.dialogUpdate) {
				this.$emit("update:dialog", uuid);
				return false;
			}
			this.$router.push({
				name: this.updateRoute,
				params: { uuid },
				query: { t: new Date().getTime() },
			});
		},
		deleteClear() {
			this.deleteDialog = false;
			this.deleteUUID = null;
			this.deleteURL = null;
			this.deleteText = null;
		},
		deleteRow() {
			this.deleteButton = true;
			ApiService.delete(this.deleteURL)
				.then(() => {
					this.$emit("reload:content");
					this.$store.dispatch(CART_COUNT);
					this.deleteClear();
				})
				.catch(({ response }) => {
					console.log({ response });
				})
				.finally(() => {
					this.deleteButton = false;
				});
		},
		deleteConfirm({ uuid, barcode }) {
			this.deleteDialog = true;
			this.deleteUUID = uuid;
			this.deleteURL = this.deleteEndpoint + uuid;
			this.deleteText = barcode;
		},
		getValue(td, key) {
			const key_array = key.split(".");
			if (key_array.length > 1) {
				if (key_array[3]) {
					return td[key_array[0]][key_array[1]][key_array[2]][key_array[3]];
				}
				if (key_array[2]) {
					return td[key_array[0]][key_array[1]][key_array[2]];
				}
				if (key_array[1]) {
					return td[key_array[0]][key_array[1]];
				}
				if (key_array[0]) {
					return td[key_array[0]];
				}
			}
			return td[key] || "-";
		},
		updateSelected() {
			if (this.selectedRows && isArray(this.selectedRows)) {
				const selectedRowsLength = toSafeInteger(this.selectedRows.length);
				const tbodyLength = toSafeInteger(this.tbody.length);
				if (selectedRowsLength > 0) {
					if (!this.checkbox) {
						this.checkbox = true;
					}
					this.indeterminate = false;
					if (selectedRowsLength != tbodyLength) {
						this.indeterminate = true;
					}
				} else {
					this.selectedRows = [];
					this.checkbox = false;
					this.indeterminate = false;
				}
				this.$store.commit(SET_SELECTED_ROWS, this.selectedRows);
			} else {
				this.$store.commit(SET_SELECTED_ROWS, []);
			}
		},
		selectAll() {
			this.selectedRows = [];
			if (this.checkbox) {
				for (let i = 0; i < this.tbody.length; i++) {
					this.selectedRows.push(this.tbody[i].uuid);
				}
			}
			this.$store.commit(SET_SELECTED_ROWS, this.selectedRows);
		},
		createCheckInOut(data) {
			this.$emit("createCheckInOut", data);
		},
		is_edit_disabled(row) {
			if (this.type == "service") {
				if (row.status == 2) {
					return true;
				}
			}
			if (this.type == "incharge-officer") {
				if (row.is_resigned == 1) {
					return true;
				}
			}
			return false;
		},
		is_checkin(row) {
			if (this.type == "checkout") {
				if (row.inventory > 0) {
					return false;
				}
			}
			return true;
		},
		is_delete_disabled(row) {
			if (this.type == "service") {
				if (row.status == 2) {
					return true;
				}
			}
			return false;
		},
	},
	components: {
		Chip,
		Status,
		Dialog,
		ShowValue,
		ImageTemplate,
		TableDateTime,
		//Barcode,
	},
	computed: {
		...mapGetters([
			"selected",
			"tbody",
			"currentPage",
			"totalPage",
			"showingString",
			"thead",
			"sortedThead",
		]),
	},
};
</script>
